import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { transportApi, downloadCsv } from '@/services/transportApi';
import { OwnershipFilter, ownershipParams } from '@/components/transport/DashboardBits';
import { ReportKpis } from '@/components/transport/reports/ReportKpis';
import { LoadUnloadReport } from '@/components/transport/reports/LoadUnloadReport';
import { FuelEfficiencyReport } from '@/components/transport/reports/FuelEfficiencyReport';
import { MonthlyTrendReport } from '@/components/transport/reports/MonthlyTrendReport';
import { DriverMileageReport } from '@/components/transport/reports/DriverMileageReport';

const Reports = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('day');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [own, setOwn] = useState('all');
  const [luGroup, setLuGroup] = useState('place');
  const [trendVeh, setTrendVeh] = useState('all');
  const [data, setData] = useState({});

  const params = useCallback(() => {
    const p = { ...ownershipParams(own) }; if (from) p.date_from = from; if (to) p.date_to = to; return p;
  }, [from, to, own]);
  const load = useCallback(async () => {
    const p = params();
    try {
      const [dw, vw, dr, rd, lu, fe, dm] = await Promise.all([
        transportApi.reportDayWise(p), transportApi.reportVehicleWise(p),
        transportApi.reportDriverRanking(p), transportApi.reportRouteDeviation(p),
        transportApi.reportLoadUnload(p), transportApi.reportFuelEfficiency(p),
        transportApi.reportDriverMileage(p),
      ]);
      setData({ dw, vw, dr, rd, lu, fe, dm });
    } catch (e) { toast.error('Failed to load reports'); }
  }, [params]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setTrendVeh('all'); }, [own]);

  const exportCsv = async (name) => {
    const map = { day: 'day-wise', vehicle: 'vehicle-wise', driver: 'driver-ranking', route: 'route-deviation', load: 'load-unload', fuel: 'fuel-efficiency', trend: 'monthly-trend', dmileage: 'driver-mileage' };
    const extra = name === 'load' ? { group: luGroup } : name === 'trend' && trendVeh !== 'all' ? { vehicle_id: trendVeh } : {};
    const qs = new URLSearchParams({ ...params(), ...extra, format: 'csv' }).toString();
    const suffix = (name === 'load' ? `_by_${luGroup}` : '') + (own !== 'all' ? `_${own}` : '');
    try { await downloadCsv(`/transport/reports/${map[name]}?${qs}`, `transport_${map[name]}${suffix}.csv`); }
    catch { toast.error('Export failed'); }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="transport-reports-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg print:hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/sections/transport')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button"><ArrowLeft className="w-5 h-5" /></Button>
          <h1 className="text-xl font-bold font-display">Transport Reports & KPIs</h1>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex flex-wrap items-end gap-3 mb-4 print:hidden">
          <div><label className="text-xs text-gray-500">From</label><Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="max-w-[160px]" data-testid="report-from-input" /></div>
          <div><label className="text-xs text-gray-500">To</label><Input type="date" value={to} onChange={e => setTo(e.target.value)} className="max-w-[160px]" data-testid="report-to-input" /></div>
          <OwnershipFilter value={own} onChange={setOwn} testPrefix="report" />
          <Button variant="outline" onClick={() => exportCsv(tab)} data-testid="report-export-btn"><Download className="w-4 h-4 mr-1" />Export CSV</Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="report-print-btn"><Printer className="w-4 h-4 mr-1" />Print</Button>
        </div>

        <ReportKpis lu={data.lu} fe={data.fe} />

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="print:hidden flex-wrap h-auto" data-testid="report-tabs">
            <TabsTrigger value="day" data-testid="tab-day">Day-wise</TabsTrigger>
            <TabsTrigger value="vehicle" data-testid="tab-vehicle">Vehicle-wise</TabsTrigger>
            <TabsTrigger value="driver" data-testid="tab-driver">Driver Ranking</TabsTrigger>
            <TabsTrigger value="dmileage" data-testid="tab-driver-mileage">Driver Mileage</TabsTrigger>
            <TabsTrigger value="route" data-testid="tab-route">Route Deviation</TabsTrigger>
            <TabsTrigger value="load" data-testid="tab-load">Loading / Unloading</TabsTrigger>
            <TabsTrigger value="fuel" data-testid="tab-fuel">Fuel Efficiency</TabsTrigger>
            <TabsTrigger value="trend" data-testid="tab-trend">Monthly Trend</TabsTrigger>
          </TabsList>

          <TabsContent value="dmileage">
            <DriverMileageReport data={data.dm} />
          </TabsContent>
          <TabsContent value="trend">
            <MonthlyTrendReport own={own} vehicleId={trendVeh} onVehicle={setTrendVeh} />
          </TabsContent>

          <TabsContent value="load">
            <LoadUnloadReport data={data.lu} group={luGroup} onGroup={setLuGroup} />
          </TabsContent>
          <TabsContent value="fuel">
            <FuelEfficiencyReport data={data.fe} />
          </TabsContent>
          <TabsContent value="day">
            <ReportTable testId="day-report-table" headers={['Date', 'Trips', 'Distance (km)', 'Empty Runs', 'Empty-run km', 'Fuel ₹', 'Service ₹', 'Total Running ₹']}
              rows={(data.dw?.rows || []).map(r => [r.date, r.trips, r.distance, r.empty_trips ?? 0, r.empty_km ?? 0, r.fuel_cost, r.service_cost, r.total_cost])} empty="No activity in range." />
          </TabsContent>
          <TabsContent value="vehicle">
            <ReportTable testId="vehicle-report-table" headers={['Vehicle', 'Type', 'Owned/Hired', 'Trips', 'Distance', 'Empty-run km', 'Litres', 'Km/L (fuel odo)', 'Fuel ₹', 'Service ₹', '₹/km', 'Total ₹']}
              rows={(data.vw?.rows || []).map(r => [r.registration_no, r.vehicle_type, r.ownership === 'hired' ? 'Hired' : 'Owned', r.trips, r.distance, r.empty_km ?? 0, r.litres, r.fuel_economy ?? '—', r.fuel_cost, r.service_cost, r.per_km_cost ?? '—', r.total_cost])} empty="No vehicles." />
          </TabsContent>
          <TabsContent value="driver">
            {data.dr && <p className="text-sm text-gray-500 mb-2" data-testid="driver-fleet-avg">Fleet average this month: <strong>{data.dr.fleet_avg_this_month} km</strong></p>}
            <ReportTable testId="driver-report-table" headers={['Driver', 'Trips', 'Distance', 'Empty-run km', 'Attendance %', 'KM This Month', 'KM vs Fleet Avg']}
              rows={(data.dr?.rows || []).map(r => [r.driver_name, r.trips, r.distance, r.empty_km ?? 0, `${r.attendance_pct}%`, r.km_this_month,
                (r.km_vs_fleet_avg > 0 ? '+' : '') + r.km_vs_fleet_avg])} empty="No drivers." />
          </TabsContent>
          <TabsContent value="route">
            {data.rd && <p className="text-sm text-gray-500 mb-2">Deviation threshold: <strong>±{data.rd.threshold_pct}%</strong> (change in Settings)</p>}
            <Card className="border-0 shadow-md"><CardContent className="p-0">
              <Table data-testid="route-report-table">
                <TableHeader><TableRow>
                  <TableHead>Date</TableHead><TableHead>Route</TableHead><TableHead>Type</TableHead><TableHead>Actual (min)</TableHead><TableHead>Benchmark (min)</TableHead><TableHead>Deviation</TableHead><TableHead>Flag</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(data.rd?.rows || []).map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>{r.date}</TableCell><TableCell className="text-sm">{r.pickup} → {r.drop}</TableCell><TableCell>{r.vehicle_type}</TableCell>
                      <TableCell>{r.actual_minutes}</TableCell><TableCell>{r.benchmark_minutes ?? '—'}</TableCell>
                      <TableCell>{r.deviation_pct != null ? `${r.deviation_pct}%` : '—'}</TableCell>
                      <TableCell>{r.flag === 'SLOW' ? <Badge className="bg-red-100 text-red-700">Slow</Badge> : r.flag === 'FAST' ? <Badge className="bg-blue-100 text-blue-700">Fast</Badge> : '—'}</TableCell>
                    </TableRow>
                  ))}
                  {(!data.rd?.rows || data.rd.rows.length === 0) && <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8" data-testid="route-report-empty">No completed trips with a benchmark yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

const ReportTable = ({ headers, rows, empty, testId }) => (
  <Card className="border-0 shadow-md"><CardContent className="p-0">
    <Table data-testid={testId}>
      <TableHeader><TableRow>{headers.map(h => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
      <TableBody>
        {rows.map((r, i) => <TableRow key={i}>{r.map((c, j) => <TableCell key={j}>{c}</TableCell>)}</TableRow>)}
        {rows.length === 0 && <TableRow><TableCell colSpan={headers.length} className="text-center text-gray-400 py-8">{empty}</TableCell></TableRow>}
      </TableBody>
    </Table>
  </CardContent></Card>
);

export default Reports;
